// StepFour.jsx
import React from "react";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Avatar,
  Checkbox,
  FormControlLabel,
  Divider,
  Fab,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ReactComponent as DeleteOutlineIcon } from "../../../../assets/DeleteSvg.svg";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import UploadIcon from "@mui/icons-material/Upload";
import {
  useFormContext,
  Controller,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import { Add } from "@mui/icons-material";

import { ReactComponent as DeltaTechSvg } from "../../../../assets/DeltaTech.svg";

import { ReactComponent as UploadFileSvg } from "../../../../assets/UploadFile.svg";
import { ReactComponent as FileUploadPlaceholderSvg } from "../../../../assets/FileUploadPlaceholder.svg";


const makeDefaultUser = () => ({
  firstName: "",
  lastName: "",
  email: "",
  status: "aktiviert",
  mobile: "",
  phone: "",
  autoInvite: false,
  avatar: "", // Data URL once chosen
});

export default function StepFour() {

  const { control, getValues } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "step_four.users",
  });

  const [expanded, setExpanded] = React.useState(-1);

  const handleAddUser = () => {

    const idx = fields.length;
    append(makeDefaultUser());
    setExpanded(idx);
  };

  return (
    <Box className="step4">
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Stack sx={{ mb: 2 }}>

          <Typography className="textSecondary" sx={{ fontWeight: 500, mb: 2 }}>
            <DeltaTechSvg /> {getValues("step_one.customerName") || "Company Name"}
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 500, color: "var(--color-primary)" }}>
            User
          </Typography>
          <Typography className="textSecondary" sx={{ fontWeight: 500 }}>
            Ein Kunde kann mehrere Userinnen und User haben. Dieser Schritt ist
            optional – Sie können später Benutzer einladen.
          </Typography>
        </Stack>

        {fields.map((field, idx) => (
          <UserRow
            key={field.id}
            index={idx}
            onRemove={() => {
              remove(idx);
              setExpanded((prev) => (prev > idx ? prev - 1 : prev === idx ? -1 : prev));
            }}
            expanded={expanded === idx}
            setExpanded={(open) => setExpanded(open ? idx : -1)}
          />
        ))}

        <br />

        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={handleAddUser}
        >
          User hinzufügen
        </Button>
      </Paper>
    </Box>
  );
}

/* ---------------------- Row component (two-column layout) ---------------------- */
function UserRow({ index, onRemove, expanded, setExpanded }) {
  const { control, setValue } = useFormContext();

  const first = useWatch({ control, name: `step_four.users.${index}.firstName` }) || "";
  const last = useWatch({ control, name: `step_four.users.${index}.lastName` }) || "";
  const avatarSrc = useWatch({ control, name: `step_four.users.${index}.avatar` }) || "";
  const title = `${first} ${last}`.trim() || "Neue Person";

  const onAvatarPick = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setValue(`step_four.users.${index}.avatar`, e.target?.result || "", {
        shouldDirty: true,
        shouldValidate: false,
      });
    };
    reader.readAsDataURL(file); // store as data URL so Avatar can preview
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, open) => setExpanded(open)}
      disableGutters
      sx={{
        mb: 2,
        border: "1px solid #E6E8EB",
        borderRadius: 2,
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Typography sx={{ fontWeight: 500, color: "var(--color-primary)" }}>{title}</Typography>

          <IconButton
            aria-label="Entfernen"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0 }}>
        <Grid container spacing={4} alignItems="flex-start">
          {/* LEFT: fields (9 cols) */}
          <Grid item xs={12} md={9}>
            <Grid container spacing={4}>
              {/* Vorname / Nachname */}
              <Grid item xs={12} md={6}>
                <Controller
                  name={`step_four.users.${index}.firstName`}
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} fullWidth label="Vorname" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name={`step_four.users.${index}.lastName`}
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} fullWidth label="Nachname" />
                  )}
                />
              </Grid>

              {/* E-Mail / Status */}
              <Grid item xs={12} md={6}>
                <Controller
                  name={`step_four.users.${index}.email`}
                  control={control}
                  rules={{
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Ungültige E-Mail-Adresse",
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="E-Mail-Adresse"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name={`step_four.users.${index}.status`}
                  control={control}
                  render={({ field }) => (
                    <>
                      <TextField {...field} select fullWidth label="User-Account-Status">
                        <MenuItem value="aktiviert">aktiviert</MenuItem>
                        <MenuItem value="deaktiviert">deaktiviert</MenuItem>
                      </TextField>

                    </>
                  )}
                />
              </Grid>

              {/* Handy / Festnetz */}
              <Grid item xs={12} md={6}>
                <Controller
                  name={`step_four.users.${index}.mobile`}
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} fullWidth label="Handynummer" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name={`step_four.users.${index}.phone`}
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} fullWidth label="Festnetznummer (optional)" />
                  )}
                />
              </Grid>

              {/* Invite section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" className="fRubik" sx={{ mb: 0.5 }}>
                  Einladungslink senden
                </Typography>
                <Typography className="textSecondary" color="text.secondary" sx={{ mb: 1 }}>
                  Senden Sie dieser Person eine E-Mail mit einem Einladungslink zur Mitarbeit. Beim ersten
                  Login vergibt die eingeladene Person ihr Passwort selbstständig. Der Einladungslink kann
                  jederzeit gesendet werden.
                </Typography>
                <Controller
                  name={`step_four.users.${index}.autoInvite`}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={!!field.value} />}
                      label="Einladungslink automatisch versenden nach Erstellung"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* RIGHT: avatar upload (3 cols) */}
          <Grid item xs={12} md={3}>
            <Box sx={{ position: "relative", display: "grid", placeItems: "center", height: 100 }}>
              {
                avatarSrc ? <Avatar
                  src={avatarSrc || FileUploadPlaceholderSvg}
                  sx={{ width: 112, height: 112, boxShadow: 1 }}
                /> : <FileUploadPlaceholderSvg />
              }
              <Tooltip title="Bild hochladen">
                <label
                  htmlFor={`user-avatar-${index}`}
                  style={{ position: "absolute", right: 35, bottom: 3 }}
                >
                  <input
                    id={`user-avatar-${index}`}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => onAvatarPick(e.target.files?.[0] || null)}
                  />
                  <Fab
                    size="small"
                    className="bxShadow circleRadius"
                    component="span"
                    sx={{ boxShadow: 2, backgroundColor: "#FFFFFF" }}
                  >
                    <UploadFileSvg />
                  </Fab>
                </label>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>

  );
}
